import { useThree } from "@react-three/fiber";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OrbitControls } from '@react-three/drei';
import { useVehicle, useAssistance } from "../../hooks";
import { gsap } from "gsap"
import { Human } from "../common/3d/Human";
import { Jaguar } from "../common/3d/Jaguar";
const SceneViewer = () => {
  const { vehicle } = useVehicle();
  const {assistance} = useAssistance();
  const { camera } = useThree();

  const [displayCrash, setDisplayCrash] = useState(false);

  const initializeCamera = useCallback((camera) => {
    camera.position.set(0, 10, 0.01);
    camera.fov = 30;
    camera.updateProjectionMatrix();
  }, []);

  const engine = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: 0,
        y: 1.3,
        z: 6.5,
        duration: 5,
        ease: "power4.inOut"
      });
  }, []);

  const frontLeftTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: -5,
        y: 1,
        z: -4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);

  const frontRightTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: 5,
        y: 1,
        z: -4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);


  const rearLeftTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: -5,
        y: 1,
        z: 4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);


  const rearRightTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: 5,
        y: 1,
        z: 4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);

  const cameraMovements = useMemo(() =>
    ({ initializeCamera, engine, frontLeftTire, frontRightTire, rearLeftTire, rearRightTire }),
    [initializeCamera, engine, frontLeftTire, frontRightTire, rearLeftTire, rearRightTire]);


  const getBreakdownCategory = useCallback(() => {
    if (vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT >= 120) {
      return "engine";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow) {
      return "frontLeftTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow) {
      return "frontRightTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow) {
      return "rearLeftTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow) {
      return "rearRightTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn) {
      return "frontLeftTireBrakeWorn";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn) {
      return "frontRightTireBrakeWorn";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn) {
      return "rearLeftTireBrakeWorn";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn) {
      return "rearRightTireBrakeWorn";
    }
    return null;
  }, [
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow, vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT
  ]);

  
  useEffect(() => {
    // console.log("salem", assistance)
    if (assistance?.category === "ecall") {
      setDisplayCrash(true);  
    } else {
      setDisplayCrash(false);
    }
  }, [assistance?.category]); 


  useEffect(() => {

    const breakdownCategory = getBreakdownCategory();

    cameraMovements.initializeCamera(camera);

    switch (breakdownCategory) {
      case "engine":
        cameraMovements.engine(camera);
        break;
      case "battery":
        cameraMovements.engine(camera); //TODO: To ReDefine
        break;
      case "frontLeftTireLowPressure":
        break;
      case "frontLeftTireBrakeWorn":
        cameraMovements.frontLeftTire(camera);
        break;
      case "frontRightTireLowPressure":
        break;
      case "frontRightTireBrakeWorn":
        cameraMovements.frontRightTire(camera);
        break;
      case "rearLeftTireLowPressure":
        break;
      case "rearLeftTireBrakeWorn":
        cameraMovements.rearLeftTire(camera);
        break;
      case "rearRightTireLowPressure":
      case "rearRightTireBrakeWorn":
        cameraMovements.rearRightTire(camera);
        break;
      default:
        cameraMovements.engine(camera);
        break
    }

  }, [camera, cameraMovements, getBreakdownCategory]);


  return (
    <React.Fragment >
      <OrbitControls maxPolarAngle={Math.PI / 2} />
      <Jaguar vehicle={vehicle} position={[0, -1, -1.4]} rotation={[0, Math.PI, 0]} isCrashed={displayCrash} ></Jaguar> {/* Here, we reposition the vehicle because, according to AVSimulations, the vehicle's origin is located on the rear axle.*/}
      {vehicle?.Vehicle?.Occupant?.Row1?.DriverSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row1?.DriverSide?.Activity} position={[0.5, -0.25, 0.7]} isCrashed={displayCrash}> </Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row1?.PassengerSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row1?.PassengerSide?.Activity} position={[-0.5, -0.25, 0.7]}  isCrashed={displayCrash}  ></Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row2?.DriverSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row2?.DriverSide?.Activity} position={[0.5, -0.25, -0.1]} isCrashed={displayCrash} ></Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row2?.PassengerSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row2?.PassengerSide?.Activity} position={[-0.5, -0.25, -0.1]} isCrashed={displayCrash} ></Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row2?.Middle?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row2?.Middle?.Activity} position={[0, -0.25, -0.1]} isCrashed={displayCrash} ></Human>
      )}
      <ambientLight args={[0xffffff, 5]} />
      <directionalLight args={[0xffffff, 2]} position={[0, 0, 5]} />
      <directionalLight args={[0xffffff, 2]} position={[0, 0, -5]} />
      <directionalLight args={[0xffffff, 2]} position={[0, 5, 0]} />
      <directionalLight args={[0xffffff, 2]} position={[-5, 0, -5]} />
      <directionalLight args={[0xffffff, 2]} position={[5, 0, -5]} />

    </React.Fragment>
  );
}

export default React.memo(SceneViewer)